import React, { useState, useRef } from 'react';
import { MessageCircle } from 'lucide-react';
import InputArea from './chat_interface/components/InputArea';
import ResponseDisplay from './chat_interface/components/ResponseDisplay';
import ChatControls from './chat_interface/components/ChatControls';
import ConnectionStatusIndicator from './chat_interface/components/ConnectionStatusIndicator';
import { useChatApi } from './chat_interface/api/index.js';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: {
    suggestions?: string[];
    type?: string;
    data?: any;
  };
}

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const inputAreaRef = useRef<{ setValue: (value: string) => void; clear: () => void; focus: () => void }>(null);
  
  
  // Initialize the chat API with correct backend URL
  const chatApi = useChatApi({
    apiUrl: 'http://localhost:8000/api/v1/chat/',
    streaming: false
  }) as {
    sendMessage: (message: string, context?: any) => Promise<any>;
    sendFeedback: (messageId: string, feedback: string, comment?: string) => Promise<any>;
    sendStreamingMessage: (message: string, onChunk: Function, context?: any) => Promise<any>;
  };

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    // Add user message to state
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    try {
      // Send message to backend
      const response = await chatApi.sendMessage(message);
      
      // Add assistant response to state
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.message || 'No response received',
        timestamp: new Date().toISOString(),
        metadata: {
          suggestions: response.suggestions || [],
          type: 'restaurant_response',
          data: response.restaurants?.[0] || null
        }
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please check if the backend server is running.');
      
      // Add error message to chat
      const errorMessage: Message = {
        id: (Date.now() + 2).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error while processing your request. Please try again.',
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAttachment = (files: File[]) => {
    console.log('Files attached:', files);
    // TODO: Implement file attachment logic
  };


  const handleClearChat = () => {
    setMessages([]);
    setError(null);
    setIsExpanded(false); // Return to icon state when clearing
    // Clear the input field
    if (inputAreaRef.current) {
      inputAreaRef.current.clear();
    }
  };

  const handleScrollToBottom = () => {
    // This will be called by ChatControls and forwarded to ResponseDisplay
    const responseDisplay = document.querySelector('[data-testid="response-display"]') as HTMLElement;
    if (responseDisplay) {
      responseDisplay.scrollTo({
        top: responseDisplay.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const handleSelectSuggestion = (suggestion: string) => {
    // Use ref to set value in InputArea without causing App re-render
    if (inputAreaRef.current) {
      inputAreaRef.current.setValue(suggestion);
    }
  };


  const handleIconClick = () => {
    setIsExpanded(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsExpanded(true);
    }
  };

  // ChatBot Icon Component
  const ChatBotIcon = () => (
    <button
      data-testid="chatbot-icon"
      onClick={handleIconClick}
      onKeyDown={handleKeyDown}
      className="
        fixed bottom-6 right-6 z-50
        w-14 h-14 rounded-full
        bg-primary hover:bg-primary-dark
        shadow-lg hover:shadow-xl
        flex items-center justify-center
        transition-all duration-200
        hover:scale-110
        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
      "
      aria-label="Open chat interface"
      tabIndex={0}
    >
      <MessageCircle 
        data-testid="chat-icon-svg"
        className="w-6 h-6 text-white" 
      />
    </button>
  );

  // Full Chat Interface Component
  const ChatFullInterface = () => (
    <div 
      data-testid="chat-full-interface"
      className="
        fixed bottom-6 right-6 z-50
        w-96 h-[600px]
        animate-in slide-in-from-bottom-2 duration-300
        bg-white rounded-lg shadow-2xl
        flex flex-col overflow-hidden
        transform-gpu will-change-transform
      "
    >
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-border-gray flex-shrink-0">
        <div className="px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-lg font-bold text-primary">AgileAIDev ChatBot</h1>
            <p className="text-gray-600 text-sm flex items-center">
              AI-Powered Conversational Interface
              <ConnectionStatusIndicator apiUrl="http://localhost:8000" />
            </p>
          </div>
          <img 
            src="https://agileaidev.com/Portals/0/AgileAidev.png?ver=P2z0NUTEAoEQcaGviGVA5A%3d%3d"
            alt="AgileAIDev Logo"
            className="h-32"
            loading="lazy"
            decoding="async"
          />
        </div>
      </header>
      
      {/* Chat Interface Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="p-4 flex-shrink-0">
          <h2 className="text-lg font-semibold mb-2">Chat Interface</h2>
          <p className="text-gray-600 text-sm">
            The AI Powered ChatBot!
          </p>
          
          {/* Error Display */}
          {error && (
            <div className="bg-red-100 border border-red-300 text-red-700 px-3 py-2 rounded mt-2 text-sm">
              {error}
            </div>
          )}
        </div>
        
        {/* Chat Messages Area */}
        <div className="flex-1 border-t border-border-gray flex flex-col min-h-0">
          <ResponseDisplay 
            messages={messages}
            loading={isLoading}
            error={error ? { message: error } : null}
            enableFeedback={true}
            enableRestaurantActions={true}
            enableSharing={true}
            onSelectSuggestion={handleSelectSuggestion}
          />
        </div>
        
        {/* Chat Controls */}
        <div className="flex-shrink-0">
          <ChatControls
            onClearChat={handleClearChat}
            onScrollToBottom={handleScrollToBottom}
            confirmClear={true}
            disabled={isLoading}
            showScrollButton={false}
          />
        </div>


        {/* Chat Input Area */}
        <div className="border-t border-border-gray flex-shrink-0">
          <InputArea
            ref={inputAreaRef}
            onSendMessage={handleSendMessage}
            onAttachment={handleAttachment}
            placeholder="Customized ChatBots!"
            allowAttachments={true}
            disabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 font-museo">
      {/* Render either icon or full interface based on state */}
      {!isExpanded && <ChatBotIcon />}
      {isExpanded && <ChatFullInterface />}
    </div>
  );
}

export default App; 